import { AssignmentItem } from "../../../../common/components/custom/assignment-clause-card/AssignmentMainContent";
import { formattedDate } from "../../../../common/utils/date";
import { HttpClient } from "../../../../common/services/http-client.service";
import { ApiResult } from "../../../../common/models";
import { flowConfigConfig } from "../config/flow-config.config";

export interface FlowConfigResponse {
  config_id: number | null;
  created_by: string;
  flow_name: string;
  flow_description: string;
  upload_method?: string; // Optional since it might not be in all responses
  sharepoint_url?: string; // Optional since it might not be in all responses
  config_name?: string; // Optional field for extraction configuration name
  document_type?: string; // Optional field for document type
  ingestion: boolean;
  extraction: boolean;
  tagging: boolean;
  similarity: boolean;
  export: boolean;
  tag_configuration?: string; // Optional field for tag configuration
  created_time: string;
  id: number;
  flow_id?: number; // Add flow_id for document type API response
}

export interface CreateFlowConfigRequest {
  config_id: number;
  created_by: string;
  created_by_email: string;
  flow_name: string;
  flow_description: string;
  upload_method?: string; // Optional field for upload method
  sharepoint_url?: string; // Optional field for SharePoint URL
  document_type?: string; // Add document_type field to store the document type
  ingestion: boolean;
  extraction: boolean;
  tagging: boolean;
  similarity: boolean;
  export: boolean;
  tag_configuration?: string; // Optional field for tag configuration
  created_time: string;
}

export interface UpdateFlowConfigRequest {
  config_id: number;
  created_by: string;
  created_by_email: string;
  flow_name: string;
  flow_description: string;
  upload_method?: string; // Optional field for upload method
  sharepoint_url?: string; // Optional field for SharePoint URL
  document_type?: string; // Add document_type field to store the document type
  ingestion: boolean;
  extraction: boolean;
  tagging: boolean;
  similarity: boolean;
  export: boolean;
  created_time: string;
}

export class FlowConfigService extends HttpClient {
  private static readonly BASE_URL = `/gdx/database/flowconfig`;

  static async getAllFlowConfigurations(
    documentType = "Engineering"
  ): Promise<ApiResult<FlowConfigResponse[]>> {
    try {
      const typeConfig =
        flowConfigConfig.getFlowConfigurationsByDocumentType(documentType);
      const response = await this.invokeApi<FlowConfigResponse[]>(typeConfig);

      return new ApiResult<FlowConfigResponse[]>(
        response.value || [],
        response.statusCode,
        ...response.errors
      );
    } catch (error) {
      console.error("Error fetching flow configurations:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return new ApiResult<FlowConfigResponse[]>([] as any, 500, errorMessage);
    }
  }

  static async createFlowConfiguration(
    flowConfig: CreateFlowConfigRequest,
    headers: Record<string, string> = {}
  ): Promise<ApiResult<FlowConfigResponse>> {
    const config = flowConfigConfig.createFlowConfiguration(flowConfig);
    try {
      const response = await this.invokeApi<FlowConfigResponse>(
        config,
        headers
      );

      return new ApiResult<FlowConfigResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    } catch (error) {
      console.error("Error creating flow configuration:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return new ApiResult<FlowConfigResponse>({} as any, 500, errorMessage);
    }
  }

  static async deleteFlowConfiguration(
    flowConfigId: number
  ): Promise<ApiResult<FlowConfigResponse>> {
    const config = flowConfigConfig.deleteFlowConfiguration(flowConfigId);
    try {
      const response = await this.invokeApi<FlowConfigResponse>(config);

      return new ApiResult<FlowConfigResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    } catch (error) {
      console.error("Error deleting flow configuration:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return new ApiResult<FlowConfigResponse>({} as any, 500, errorMessage);
    }
  }

  static async updateFlowConfiguration(
    flowConfigId: number,
    flowConfig: UpdateFlowConfigRequest
  ): Promise<ApiResult<FlowConfigResponse>> {
    const config = flowConfigConfig.updateFlowConfiguration(
      flowConfigId,
      flowConfig
    );
    try {
      const response = await this.invokeApi<FlowConfigResponse>(config);

      return new ApiResult<FlowConfigResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    } catch (error) {
      console.error("Error updating flow configuration:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return new ApiResult<FlowConfigResponse>({} as any, 500, errorMessage);
    }
  }

  static async getFlowConfigurationById(
    flowConfigId: number
  ): Promise<ApiResult<FlowConfigResponse>> {
    const config = flowConfigConfig.getFlowConfigurationById(flowConfigId);
    try {
      const response = await this.invokeApi<FlowConfigResponse>(config);

      return new ApiResult<FlowConfigResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    } catch (error) {
      console.error("Error fetching flow configuration by ID:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return new ApiResult<FlowConfigResponse>({} as any, 500, errorMessage);
    }
  }

  static transformToAssignmentItems(
    flowConfigs: FlowConfigResponse[]
  ): AssignmentItem[] {
    return flowConfigs.map((config) => {
      // Apply "Others" categorization logic
      const standardDocTypes = ["Engineering", "Contract", "Invoice"];
      let documentType = "Others"; // Default for flow configs without document_type

      // If the config has a document_type field, use the categorization logic
      if (config.document_type && config.document_type.trim() !== "") {
        const configDocType = config.document_type;
        documentType = standardDocTypes.includes(configDocType)
          ? configDocType
          : "Others";
      }
      // If no document_type or empty, keep as "Others" since these configs
      // are already filtered to only appear in "Others" category

      return {
        id: config.flow_id || config.id, // Use flow_id first, fallback to id
        title: config.flow_name,
        status: this.determineStatus(config),
        company: "EY", // Default company since not provided in API
        department: "Technology", // Default department since not provided in API
        createdBy: config.created_by,
        createdOn: this.formatDate(config.created_time),
        documentType: documentType
      };
    });
  }

  private static determineStatus(config: FlowConfigResponse): string {
    // Determine status based on which operations are enabled
    const enabledOperations = [
      config.ingestion,
      config.extraction,
      config.tagging,
      config.similarity,
      config.export
    ].filter(Boolean).length;

    if (enabledOperations === 0) {
      return "Draft";
    } else if (enabledOperations < 3) {
      return "In Progress";
    } else {
      return "Active";
    }
  }

  private static formatDate(dateString: string): string {
    try {
      return formattedDate(dateString);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  }
}
